import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cn } from '../../lib/cn.js'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-hover disabled:bg-border-strong',
  secondary:
    'bg-surface-raised text-text border border-border hover:bg-surface-sunken disabled:text-text-muted',
  ghost: 'text-text-muted hover:bg-surface-sunken hover:text-text',
  danger: 'bg-danger text-white hover:bg-danger-hover disabled:bg-border-strong',
}

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm',
  md: 'h-10 px-4 text-sm',
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  children: ReactNode
}

/**
 * The classes a button wears, for the rare case a link has to look like one.
 *
 * A navigation that renders as a `<button>` breaks the middle-click, the
 * context menu and "open in new tab" — so the element stays an anchor and only
 * borrows the appearance.
 */
export function buttonClasses(variant: Variant = 'primary', size: Size = 'md'): string {
  return cn(
    'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
    variants[variant],
    sizes[size],
  )
}

export function Button({
  variant = 'primary',
  size = 'md',
  className,
  type = 'button',
  ...props
}: ButtonProps) {
  return (
    <button
      // Defaults to "button": a bare <button> inside a form submits it, which
      // has surprised people out of half-finished forms often enough.
      type={type}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-70',
        variants[variant],
        sizes[size],
        className,
      )}
      {...props}
    />
  )
}
